import { Controller } from "@hotwired/stimulus"

/**
 * Generic agent discovery controller for conversational LLM flows.
 *
 * Watches chat messages for a sentinel tag, extracts the JSON config,
 * renders a preview, and saves the config to a create URL.
 *
 * Configuration via Stimulus values:
 *   - sentinelTag: The uppercase tag name (e.g., "INTEGRATION_CONFIG", "SOURCE_CONNECTOR_CONFIG")
 *   - createUrl: The POST URL for saving the discovered config
 *   - bodyKey: The JSON body key for the config (e.g., "api_integration", "source_connector")
 *   - executionId: The agent execution ID
 *   - resourceId: Optional existing resource ID (for re-analysis/update flows)
 *   - resourceIdKey: The body key for the resource ID (e.g., "source_connector_id")
 *   - existingConfig: Optional JSON string of existing config to pre-populate preview (for edit/reanalyze)
 *
 * Subclasses override renderPreview(config) to provide domain-specific preview HTML.
 * The default renderPreview shows a generic key-value list.
 */
export default class extends Controller {
  static targets = ["messages", "preview", "previewContent", "saveSection"]
  static values = {
    sentinelTag: String,
    createUrl: String,
    bodyKey: String,
    executionId: Number,
    resourceId: Number,
    resourceIdKey: String,
    existingConfig: String
  }

  connect() {
    this.configData = null
    this.setupObserver()
    this.loadExistingConfig()
  }

  loadExistingConfig() {
    if (!this.hasExistingConfigValue || !this.existingConfigValue) return

    try {
      const config = JSON.parse(this.existingConfigValue)
      this.renderPreview(config)
    } catch (e) {
      // Ignore parse errors for existing config
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  setupObserver() {
    this.observer = new MutationObserver(() => {
      this.checkForConfig()
    })

    if (this.hasMessagesTarget) {
      this.observer.observe(this.messagesTarget, {
        childList: true,
        subtree: true,
        characterData: true
      })
    }
  }

  checkForConfig() {
    const tag = this.sentinelTagValue
    if (!tag) return

    const regex = new RegExp(`\\[${tag}\\]([\\s\\S]*?)\\[\\/${tag}\\]`)
    const messages = this.messagesTarget.querySelectorAll('[id^="llm_message_"]')

    for (const msg of messages) {
      const content = msg.textContent || ""
      const match = content.match(regex)
      if (match) {
        try {
          let raw = match[1].trim()
          // Strip markdown code fences if present (```json ... ```)
          raw = raw.replace(/^```(?:json)?\s*\n?/, '').replace(/\n?```\s*$/, '')
          const config = JSON.parse(raw)
          this.configData = config
          this.renderPreview(config)
          this.showSaveButton()
        } catch (e) {
          console.warn(`Failed to parse ${tag} config:`, e)
        }
      }
    }
  }

  /**
   * Override in subclass controllers to provide domain-specific preview HTML.
   * Default implementation shows a generic key-value list.
   */
  renderPreview(config) {
    if (!this.hasPreviewContentTarget) return

    let html = '<div class="space-y-3">'
    for (const [key, value] of Object.entries(config)) {
      if (value === null || value === undefined) continue
      const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      const displayValue = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)
      const isLong = displayValue.length > 100 || typeof value === 'object'

      html += `
        <div>
          <span class="text-xs font-semibold text-base-content/60 uppercase">${this.escapeHtml(label)}</span>
          ${isLong
            ? `<pre class="text-xs bg-base-200 rounded p-2 mt-1 overflow-x-auto max-h-32">${this.escapeHtml(displayValue)}</pre>`
            : `<p class="text-sm">${this.escapeHtml(displayValue)}</p>`
          }
        </div>
      `
    }
    html += '</div>'
    this.previewContentTarget.innerHTML = html
  }

  showSaveButton() {
    if (this.hasSaveSectionTarget) {
      this.saveSectionTarget.classList.remove('hidden')
    }
  }

  async save() {
    if (!this.configData) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const bodyKey = this.bodyKeyValue || 'config'

    const body = {
      [bodyKey]: this.configData,
      execution_id: this.executionIdValue
    }

    if (this.hasResourceIdValue && this.resourceIdValue) {
      const idKey = this.resourceIdKeyValue || `${bodyKey}_id`
      body[idKey] = this.resourceIdValue
    }

    try {
      const response = await fetch(this.createUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify(body)
      })

      if (response.ok) {
        const data = await response.json()
        if (data.redirect_to) {
          window.Turbo.visit(data.redirect_to)
        }
      } else {
        const errorData = await response.json()
        alert(errorData.errors?.join(', ') || 'Failed to save')
      }
    } catch (error) {
      console.error('Save failed:', error)
      alert('Failed to save')
    }
  }

  escapeHtml(str) {
    if (!str) return ''
    const div = document.createElement('div')
    div.textContent = String(str)
    return div.innerHTML
  }
}
